import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useTranslation } from 'react-i18next'

export default function ResetPassword() {
  const navigate = useNavigate()
  const { t } = useTranslation()

  // 'loading' while waiting for Supabase to process the recovery token,
  // 'ready' once a recovery session is confirmed, 'invalid' on timeout.
  const [status, setStatus] = useState('loading')

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)

  useEffect(() => {
    let timeoutId

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'INITIAL_SESSION' && session) {
        // Recovery code was exchanged before we subscribed — session is ready.
        setStatus('ready')
      } else if (event === 'INITIAL_SESSION' && !session) {
        // No session yet; PKCE exchange may still be in flight.
        timeoutId = setTimeout(() => setStatus('invalid'), 5000)
      } else if (event === 'PASSWORD_RECOVERY' && session) {
        // PKCE exchange completed after we subscribed.
        clearTimeout(timeoutId)
        setStatus('ready')
      }
    })

    return () => {
      subscription.unsubscribe()
      clearTimeout(timeoutId)
    }
  }, [])

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')

    if (password !== confirm) {
      setError(t('resetPwd.errorPasswordMatch'))
      return
    }
    if (password.length < 6) {
      setError(t('resetPwd.errorPasswordLength'))
      return
    }

    setSubmitting(true)
    const { error: updateError } = await supabase.auth.updateUser({ password })

    if (updateError) {
      setError(updateError.message)
      setSubmitting(false)
    } else {
      setDone(true)
      await supabase.auth.signOut()
      navigate('/login')
    }
  }

  /* ── Loading ── */
  if (status === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
        <div className="w-8 h-8 border-4 border-oasis-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  /* ── Invalid / expired link ── */
  if (status === 'invalid') {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
        <div className="bg-white rounded-2xl shadow-lg p-8 sm:p-12 text-center max-w-md w-full">
          <span className="text-5xl">🔗</span>
          <h2 className="mt-4 text-xl font-bold text-gray-900">{t('resetPwd.invalidTitle')}</h2>
          <p className="mt-2 text-sm text-gray-500">
            {t('resetPwd.invalidBody')}
          </p>
          <button
            onClick={() => navigate('/login')}
            className="mt-6 w-full rounded-lg bg-oasis-500 px-6 py-3 text-sm font-semibold text-white hover:bg-oasis-700 focus:outline-none focus:ring-2 focus:ring-oasis-300 transition"
          >
            {t('resetPwd.backToLogin')}
          </button>
        </div>
      </div>
    )
  }

  /* ── Success ── */
  if (done) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
        <div className="bg-white rounded-2xl shadow-lg p-8 sm:p-12 text-center max-w-md w-full">
          <span className="text-5xl">✅</span>
          <h2 className="mt-4 text-xl font-bold text-gray-900">{t('resetPwd.doneTitle')}</h2>
          <p className="mt-2 text-sm text-gray-500">{t('resetPwd.doneSubtitle')}</p>
        </div>
      </div>
    )
  }

  /* ── Set new password form ── */
  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-full bg-oasis-50 border-2 border-oasis-200 flex items-center justify-center mx-auto">
              <span className="text-oasis-700 font-black text-2xl leading-none">O</span>
            </div>
            <h1 className="mt-3 text-2xl font-bold text-gray-900">{t('resetPwd.title')}</h1>
            <p className="mt-1 text-sm text-gray-500">{t('resetPwd.subtitle')}</p>
          </div>

          {error && (
            <div className="mb-5 flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              <span className="mt-0.5 shrink-0">⚠️</span>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="password">
                {t('resetPwd.newPassword')}
              </label>
              <input
                id="password"
                type="password"
                required
                autoComplete="new-password"
                placeholder={t('resetPwd.passwordMinHint')}
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-oasis-500 focus:outline-none focus:ring-2 focus:ring-oasis-200 transition"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="confirm">
                {t('resetPwd.confirmPassword')}
              </label>
              <input
                id="confirm"
                type="password"
                required
                autoComplete="new-password"
                placeholder="••••••••"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                className={`w-full rounded-lg border px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 transition ${
                  confirm && confirm !== password
                    ? 'border-red-400 focus:border-red-400 focus:ring-red-200'
                    : 'border-gray-300 focus:border-oasis-500 focus:ring-oasis-200'
                }`}
              />
              {confirm && confirm !== password && (
                <p className="mt-1 text-xs text-red-600">{t('resetPwd.passwordsNoMatch')}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-lg bg-oasis-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-oasis-700 focus:outline-none focus:ring-2 focus:ring-oasis-300 disabled:opacity-60 disabled:cursor-not-allowed transition"
            >
              {submitting ? t('resetPwd.updating') : t('resetPwd.updatePassword')}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
